import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, CheckCircle2, XCircle, FileText, Scale, Building2, AlertTriangle } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import JsonLd from "../components/JsonLd";
import CiteThisPage from "../components/CiteThisPage";
import { usePageSEO } from "../hooks/usePageSEO";

const BASE = "https://www.peptidesformedspas.org";

const FRAMEWORKS = [
  {
    icon: Scale,
    title: "FDA — Federal Food, Drug, and Cosmetic Act",
    body:
      "Research peptides on this site are sold and labeled as research chemicals supplied for laboratory use only. They are not FDA-approved drugs, not labeled for human or animal use, and not marketed with disease-treatment, structure/function, or aesthetic-outcome claims. We do not represent any product as a substitute for an FDA-approved drug, supplement, or cosmetic.",
    items: [
      "All product listings include the FDA notice: 'These statements have not been evaluated by the FDA. Not intended to diagnose, treat, cure, or prevent any disease.'",
      "We make no claims that products will produce specific aesthetic or therapeutic outcomes",
      "We never imply FDA approval where none exists",
      "Where a separate FDA-approved drug exists for a related compound (e.g., bremelanotide), we explicitly distinguish it from research-only formulations",
    ],
  },
  {
    icon: FileText,
    title: "FTC — Truth-in-Advertising",
    body:
      "All marketing claims on this site are substantiated, written in research-only language, and disclose material relationships where they exist. Endorsements and testimonials are subject to FTC Endorsement Guides; we do not publish testimonials that imply specific therapeutic outcomes.",
    items: [
      "Product descriptions reference 'studied in laboratory research' rather than implied benefits",
      "Affiliate relationships are disclosed (catalog products link to a third-party supplier)",
      "Educational content cites limitations of the underlying research",
      "We avoid fear-based urgency, fake scarcity, and 'guaranteed results' language",
    ],
  },
  {
    icon: Building2,
    title: "State med-spa & medical-practice laws",
    body:
      "PeptidesForMedSpas is not a med spa, clinic, or pharmacy and does not operate under any state medical-direction or corporate-practice-of-medicine framework. We provide educational content. Any clinical decision belongs with a licensed clinician operating within their state's scope-of-practice and supervision rules.",
    items: [
      "We do not employ, supervise, or contract physicians, NPs, PAs, or RNs to deliver care",
      "We do not coordinate prescriptions or treatment plans for individual readers",
      "Educational referrals to partner platforms (AlphaSync Rx, MyDose.ai) do not establish a clinician-patient relationship with PeptidesForMedSpas",
      "Readers are repeatedly directed to consult licensed clinicians for any wellness or aesthetic decision",
    ],
  },
  {
    icon: ShieldCheck,
    title: "DEA — Controlled Substances Act",
    body:
      "We do not sell, list, link to, or facilitate purchase of any DEA-scheduled controlled substance. None of the compounds in our catalog appear on the federal Controlled Substances schedules.",
    items: [
      "No anabolic-androgenic steroids",
      "No narcotic, stimulant, or depressant scheduled drugs",
      "No prescription pharmaceuticals of any kind",
    ],
  },
  {
    icon: FileText,
    title: "DSHEA — Dietary Supplement Health and Education Act",
    body:
      "Research peptides on this site are not marketed as dietary supplements. Where a compound is also lawfully sold as a supplement under DSHEA elsewhere, that is a separate product class with separate labeling and separate claim restrictions; our research-only catalog is not a supplement marketing channel.",
    items: [
      "No structure/function claims under DSHEA",
      "No 'recommended daily intake' or supplement-style framing",
      "Clear separation between research chemicals and any supplement product class",
    ],
  },
  {
    icon: ShieldCheck,
    title: "HIPAA — Privacy of protected health information",
    body:
      "PeptidesForMedSpas does not collect protected health information (PHI) and is not a covered entity or business associate under HIPAA. We do not request medical history, prescriptions, or clinical records. Partner platforms (AlphaSync Rx, MyDose.ai) operate independently and apply their own privacy frameworks within the clinical workflows they support.",
    items: [
      "No medical-history forms",
      "No prescription handling or storage",
      "Standard analytics and contact-form data only — covered by our Privacy Policy",
    ],
  },
  {
    icon: FileText,
    title: "503A / 503B — Compounding pharmacy context",
    body:
      "We are not a compounding pharmacy. Where readers seek clinician-supervised compounded preparations, those preparations must be made by an FDA-registered 503A pharmacy (patient-specific prescriptions) or 503B outsourcing facility (under additional FDA oversight) — operating under licensed clinician orders. We do not interpret, recommend, or fulfill any compounded preparation.",
    items: [
      "We do not sell compounded medications",
      "We do not interface with prescriptions",
      "Educational references to compounded products always note the licensed-pharmacy and licensed-clinician requirement",
    ],
  },
  {
    icon: Scale,
    title: "State sales-tax & business registration",
    body:
      "PeptidesForMedSpas LLC is a registered Hawaii limited liability company operating from Honolulu, HI. We comply with applicable state and federal business-registration, sales-tax, and consumer-protection requirements. Where products are fulfilled by a third-party supplier (paramountpeptides.com), shipping, customs, and tax obligations are handled by that supplier under their own licensing.",
    items: [
      "PeptidesForMedSpas LLC — Hawaii registered LLC, established 2024",
      "SSL/TLS encryption on all transactions",
      "Published Privacy, Terms, Refund, and Shipping policies",
      "Point of contact: everything@8eight8.net, +1 (808) 301-2134",
    ],
  },
];

const DOS_DONTS = {
  do: [
    "Use research-only language in all product descriptions and educational content",
    "Cite study limitations and evidence quality alongside claims",
    "Direct readers to licensed clinicians for any wellness or aesthetic decision",
    "Distinguish FDA-approved pharmaceutical products from research-only formulations",
    "Maintain clear separation between our educational storefront and any clinical service",
    "Honor reader privacy — minimal data collection, no PHI",
  ],
  dont: [
    "Make therapeutic, disease-treatment, or aesthetic-outcome claims",
    "Imply FDA approval where none exists",
    "Sell prescription drugs, controlled substances, or compounded preparations",
    "Establish clinician-patient relationships with readers",
    "Publish testimonials that imply specific outcomes",
    "Use fear-based urgency, fake scarcity, or pressure tactics",
  ],
};

const CompliancePage = () => {
  usePageSEO();

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${BASE}/compliance#webpage`,
      url: `${BASE}/compliance`,
      name: "Compliance & Regulatory | PeptidesForMedSpas",
      description:
        "Our compliance posture across FDA, FTC, DEA, DSHEA, HIPAA, state med-spa rules, and 503A/503B compounding context. PeptidesForMedSpas is an educational storefront — not a pharmacy or medical provider.",
      inLanguage: "en-US",
      isPartOf: { "@id": `${BASE}/#website` },
      breadcrumb: { "@id": `${BASE}/compliance#breadcrumb` },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": `${BASE}/compliance#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${BASE}/` },
        { "@type": "ListItem", position: 2, name: "Compliance", item: `${BASE}/compliance` },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#fffaf5]" data-testid="compliance-page">
      <Header />
      <JsonLd id="compliance-jsonld" data={jsonLd} />

      {/* Hero */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-[#fdf2f4] to-[#fffaf5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-block px-4 py-2 bg-[#fdf2f4] border border-[#e8c5b8] rounded-full mb-4">
            <span className="text-sm text-[#b89a6d] font-medium">Compliance & Regulatory</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#3a2a26] mb-6" data-testid="compliance-h1">
            Compliance &amp; Regulatory
          </h1>
          <p className="text-base md:text-lg text-[#5a4a40] max-w-3xl leading-relaxed">
            PeptidesForMedSpas LLC operates as an educational storefront for research peptides.
            We comply with applicable federal and state laws governing how research chemicals are
            labeled, marketed, and sold — and we do not sell prescription medications,
            controlled substances, or compounded preparations. Below is our complete compliance posture.
          </p>
        </div>
      </section>

      {/* Frameworks grid */}
      <section className="py-12 bg-[#fffaf5]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6" data-testid="compliance-frameworks">
          {FRAMEWORKS.map((f, i) => (
            <article
              key={f.title}
              className="bg-white border border-[#f0d8d4] rounded-2xl p-7"
              data-testid={`compliance-framework-${i}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 bg-[#fdf2f4] rounded-lg flex items-center justify-center flex-shrink-0">
                  <f.icon className="w-5 h-5 text-[#b89a6d]" />
                </div>
                <h2 className="text-base md:text-lg font-bold text-[#3a2a26]">{f.title}</h2>
              </div>
              <p className="text-sm md:text-base text-[#5a4a40] leading-relaxed mb-4">{f.body}</p>
              <ul className="space-y-1.5 text-sm text-[#5a4a40]">
                {f.items.map((it) => (
                  <li key={it} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#b89a6d] mt-0.5 flex-shrink-0" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      {/* What We Do / Don't */}
      <section className="py-12 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6">
          <div className="bg-[#fffaf5] border border-[#f0d8d4] rounded-2xl p-7" data-testid="compliance-do">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle2 className="w-6 h-6 text-[#b89a6d]" />
              <h2 className="text-lg font-bold text-[#3a2a26]">What we do</h2>
            </div>
            <ul className="space-y-2 text-sm text-[#5a4a40]">
              {DOS_DONTS.do.map((d) => (
                <li key={d} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#b89a6d] mt-0.5 flex-shrink-0" />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-[#fffaf5] border border-[#f0d8d4] rounded-2xl p-7" data-testid="compliance-dont">
            <div className="flex items-center gap-3 mb-4">
              <XCircle className="w-6 h-6 text-[#b89a6d]" />
              <h2 className="text-lg font-bold text-[#3a2a26]">What we don't do</h2>
            </div>
            <ul className="space-y-2 text-sm text-[#5a4a40]">
              {DOS_DONTS.dont.map((d) => (
                <li key={d} className="flex items-start gap-2">
                  <XCircle className="w-4 h-4 text-[#b89a6d] mt-0.5 flex-shrink-0" />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* State-by-state note */}
      <section className="py-12 bg-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-[#f0d8d4] rounded-2xl p-7" data-testid="compliance-state-note">
            <div className="flex items-center gap-3 mb-3">
              <AlertTriangle className="w-6 h-6 text-[#b89a6d]" />
              <h2 className="text-lg font-bold text-[#3a2a26]">State-by-state context</h2>
            </div>
            <p className="text-sm md:text-base text-[#5a4a40] leading-relaxed mb-3">
              Med-spa, telehealth, pharmacy compounding, and licensed-clinician supervision rules
              vary significantly by state. We do not provide state-specific clinical guidance.
              Readers and clinicians should rely on:
            </p>
            <ul className="space-y-1.5 text-sm text-[#5a4a40]">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#b89a6d] mt-0.5" />
                <span>State medical board, nursing board, and pharmacy board guidance for the state in which care is delivered</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#b89a6d] mt-0.5" />
                <span>FDA.gov for current regulatory status of specific compounds (e.g., the agency's GLP-1 compounding bulletins)</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#b89a6d] mt-0.5" />
                <span>Qualified healthcare and regulatory counsel for compliance review of specific clinical or marketing programs</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Cross-links */}
      <section className="py-10 bg-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#fdf2f4] border border-[#e8c5b8] rounded-xl p-6">
            <p className="text-sm text-[#5a4a40] leading-relaxed">
              See also our{" "}
              <Link to="/disclaimer" className="text-[#b89a6d] underline hover:text-[#a08560]" data-testid="compliance-to-disclaimer">
                Disclaimer
              </Link>
              ,{" "}
              <Link to="/privacy" className="text-[#b89a6d] underline hover:text-[#a08560]" data-testid="compliance-to-privacy">
                Privacy Policy
              </Link>
              ,{" "}
              <Link to="/terms" className="text-[#b89a6d] underline hover:text-[#a08560]" data-testid="compliance-to-terms">
                Terms of Service
              </Link>
              , and the{" "}
              <Link to="/partners" className="text-[#b89a6d] underline hover:text-[#a08560]" data-testid="compliance-to-partners">
                Partners page
              </Link>{" "}
              describing AlphaSync Rx and MyDose.ai. Questions: everything@8eight8.net.
            </p>
          </div>
        </div>
      </section>

      {/* Cite this page */}
      <section className="py-10 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <CiteThisPage
            title="Compliance & Regulatory Framework — PeptidesForMedSpas"
            url={`${BASE}/compliance`}
            publishedDate="2026-04-10"
          />
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CompliancePage;
